export const AVAILABLE_CUSTOM_ROLE_PERMISSIONS = [
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
  'contact_manage',
  'report_manage',
  'knowledge_base_manage',
];

/** Per-agent feature toggles on Settings → Agents → Permissions */
export const AGENT_FEATURE_PERMISSIONS = [
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
  'conversation_assign_self',
  'conversation_assign',
  'conversation_bulk_assign',
  'contact_manage',
  'contact_export',
  'report_manage',
  'report_own',
  'report_overview',
  'report_overview_download',
  'report_conversation',
  'report_conversation_download',
  'report_agents',
  'report_agents_download',
  'report_inboxes',
  'report_inboxes_download',
  'report_teams',
  'report_teams_download',
  'report_labels',
  'report_labels_download',
  'report_csat',
  'report_csat_download',
  'report_sla',
  'report_sla_download',
  'report_bot',
  'knowledge_base_manage',
  'web_order_manage',
  'web_order_status_change',
  'web_order_edit',
  'web_order_create',
  'web_order_export',
  'web_order_products',
  'web_order_product_edit',
  'web_order_product_create',
  'web_order_customers',
  'web_order_customer_edit',
  'web_order_invoice_settings',
  'integration_manage',
  'user_manage',
];

export const DEFAULT_AGENT_FEATURE_PERMISSIONS = [
  'conversation_participating_manage',
  'conversation_assign_self',
  'contact_manage',
];

export const DEFAULT_MANAGER_FEATURE_PERMISSIONS = [
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
  'conversation_assign_self',
  'conversation_assign',
  'conversation_bulk_assign',
  'contact_manage',
  'contact_export',
  'report_manage',
  'report_own',
  'report_overview',
  'report_overview_download',
  'report_conversation',
  'report_conversation_download',
  'report_agents',
  'report_agents_download',
  'report_inboxes',
  'report_inboxes_download',
  'report_teams',
  'report_teams_download',
  'report_labels',
  'report_labels_download',
  'report_csat',
  'report_csat_download',
  'report_sla',
  'report_sla_download',
  'report_bot',
  'knowledge_base_manage',
  'web_order_manage',
  'web_order_status_change',
  'web_order_edit',
  'web_order_create',
  'web_order_export',
  'web_order_products',
  'web_order_product_edit',
  'web_order_product_create',
  'web_order_customers',
  'web_order_customer_edit',
  'web_order_invoice_settings',
  'integration_manage',
  'user_manage',
];

export const FEATURE_PERMISSION_GROUPS = [
  {
    key: 'conversations',
    title: 'Conversations',
    description: 'Control conversation visibility, assignment, and bulk work.',
    items: [
      'conversation_manage',
      'conversation_unassigned_manage',
      'conversation_participating_manage',
      'conversation_assign_self',
      'conversation_assign',
      'conversation_bulk_assign',
    ],
  },
  {
    key: 'web_orders',
    title: 'Web orders',
    description: 'Manage orders, catalog data, and invoice configuration.',
    sections: [
      {
        key: 'web_order_orders',
        title: 'Order section',
        items: [
          'web_order_manage',
          'web_order_status_change',
          'web_order_edit',
          'web_order_create',
          'web_order_export',
        ],
      },
      {
        key: 'web_order_customers_section',
        title: 'Customer section',
        items: ['web_order_customers', 'web_order_customer_edit'],
      },
      {
        key: 'web_order_products_section',
        title: 'Product section',
        items: [
          'web_order_products',
          'web_order_product_edit',
          'web_order_product_create',
        ],
      },
    ],
    items: ['web_order_invoice_settings'],
  },
  {
    key: 'reports',
    title: 'Reports',
    description: 'Control report viewing and CSV downloads.',
    sections: [
      {
        key: 'report_own_section',
        title: 'Own reports',
        items: ['report_own'],
      },
      {
        key: 'report_conversation_section',
        title: 'Conversation reports',
        items: [
          'report_overview',
          'report_conversation',
          'report_agents',
          'report_inboxes',
          'report_teams',
          'report_labels',
        ],
      },
      {
        key: 'report_download_section',
        title: 'Downloads',
        items: [
          'report_overview_download',
          'report_conversation_download',
          'report_agents_download',
          'report_inboxes_download',
          'report_teams_download',
          'report_labels_download',
          'report_csat_download',
          'report_sla_download',
        ],
      },
    ],
    items: ['report_csat', 'report_sla', 'report_bot'],
  },
  {
    key: 'operations',
    title: 'Settings & tools',
    description: 'Give access to operational tools and account management.',
    sections: [
      {
        key: 'operations_contacts',
        title: 'Contacts',
        items: ['contact_manage', 'contact_export'],
      },
      {
        key: 'operations_help_center',
        title: 'Help Center',
        items: ['knowledge_base_manage'],
      },
      {
        key: 'operations_integrations',
        title: 'Integrations',
        items: ['integration_manage'],
      },
      {
        key: 'operations_team',
        title: 'Team',
        items: ['user_manage'],
      },
    ],
  },
];

export const groupPermissionKeys = group => [
  ...(group.sections || []).flatMap(section => section.items || []),
  ...(group.items || []),
];

export const ROLES = ['agent', 'administrator', 'manager'];

export const CONVERSATION_PERMISSIONS = [
  'conversation_manage',
  'conversation_unassigned_manage',
  'conversation_participating_manage',
];

export const MANAGE_ALL_CONVERSATION_PERMISSIONS = 'conversation_manage';

export const CONVERSATION_UNASSIGNED_PERMISSIONS =
  'conversation_unassigned_manage';

export const CONVERSATION_PARTICIPATING_PERMISSIONS =
  'conversation_participating_manage';

export const CONTACT_PERMISSIONS = 'contact_manage';

export const CONTACT_EXPORT_PERMISSION = 'contact_export';

export const REPORT_VIEW_PERMISSIONS = [
  'report_own',
  'report_overview',
  'report_conversation',
  'report_agents',
  'report_inboxes',
  'report_teams',
  'report_labels',
  'report_csat',
  'report_sla',
  'report_bot',
];

export const REPORT_DOWNLOAD_PERMISSIONS = [
  'report_overview_download',
  'report_conversation_download',
  'report_agents_download',
  'report_inboxes_download',
  'report_teams_download',
  'report_labels_download',
  'report_csat_download',
  'report_sla_download',
];

export const REPORT_DOWNLOAD_TO_VIEW = {
  report_overview_download: 'report_overview',
  report_conversation_download: 'report_conversation',
  report_agents_download: 'report_agents',
  report_inboxes_download: 'report_inboxes',
  report_teams_download: 'report_teams',
  report_labels_download: 'report_labels',
  report_csat_download: 'report_csat',
  report_sla_download: 'report_sla',
};

export const REPORT_DOWNLOAD_BY_TYPE = {
  agent: 'report_agents_download',
  inbox: 'report_inboxes_download',
  team: 'report_teams_download',
  label: 'report_labels_download',
};

export const REPORT_GRANULAR_PERMISSIONS = [
  ...REPORT_VIEW_PERMISSIONS,
  ...REPORT_DOWNLOAD_PERMISSIONS,
];

export const REPORTS_PERMISSIONS = [
  'report_manage',
  ...REPORT_GRANULAR_PERMISSIONS,
];

export const PORTAL_PERMISSIONS = 'knowledge_base_manage';

export const CONVERSATION_ASSIGN_SELF_PERMISSION = 'conversation_assign_self';

export const CONVERSATION_ASSIGN_PERMISSION = 'conversation_assign';

export const CONVERSATION_BULK_ASSIGN_PERMISSION = 'conversation_bulk_assign';

export const WEB_ORDER_ACCESS_PERMISSIONS = [
  'web_order_manage',
  'web_order_status_change',
  'web_order_edit',
  'web_order_create',
];

export const WEB_ORDER_EXPORT_PERMISSION = 'web_order_export';

export const WEB_ORDER_STATUS_PERMISSIONS = [
  'web_order_status_change',
  'web_order_edit',
];

export const WEB_ORDER_CUSTOMER_ACCESS_PERMISSIONS = [
  'web_order_customers',
  'web_order_customer_edit',
];

export const WEB_ORDER_PRODUCT_ACCESS_PERMISSIONS = [
  'web_order_products',
  'web_order_product_edit',
  'web_order_product_create',
];

export const WEB_ORDER_INVOICE_SETTINGS_PERMISSION =
  'web_order_invoice_settings';

export const USER_MANAGEMENT_PERMISSION = 'user_manage';

export const INTEGRATION_MANAGEMENT_PERMISSION = 'integration_manage';

export const ASSIGNEE_TYPE_TAB_PERMISSIONS = {
  me: {
    count: 'mineCount',
    permissions: [...ROLES, ...CONVERSATION_PERMISSIONS],
  },
  unassigned: {
    count: 'unAssignedCount',
    permissions: [
      ...ROLES,
      MANAGE_ALL_CONVERSATION_PERMISSIONS,
      CONVERSATION_UNASSIGNED_PERMISSIONS,
    ],
  },
  all: {
    count: 'allCount',
    permissions: [
      ...ROLES,
      MANAGE_ALL_CONVERSATION_PERMISSIONS,
      CONVERSATION_PARTICIPATING_PERMISSIONS,
    ],
  },
};
